import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Download, Plus } from 'lucide-react';
import { format, subMonths, startOfMonth, isSameMonth } from 'date-fns';
import usePayments from '../hooks/usePayments';
import { createPayment } from '../../supabase/payments';
import { supabase } from '../../supabase';

const METHOD_OPTIONS = ['Cash', 'Venmo', 'Zelle', 'Stripe', 'Check', 'Other'];

function LogPaymentModal({ onClose }) {
  const [form, setForm] = useState({ clientName: '', amount: '', method: 'Cash', note: '', type: 'service' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amount = parseFloat(form.amount);

      const { data: client } = await supabase
        .from('clients')
        .select('id')
        .ilike('name', form.clientName.trim())
        .maybeSingle();

      await createPayment({
        ...form,
        client_id: client?.id || null,
        amount,
        status: 'paid',
        source: 'manual',
      });

      if (client?.id) {
        await supabase.rpc('increment_lifetime_value', { client_id: client.id, amount });
      }

      await supabase.from('activity_log').insert({
        note: `Manual payment logged — $${amount.toFixed(2)} from ${form.clientName} via ${form.method}`,
        type: 'payment',
        client_id: client?.id || null,
      });

      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-lg">Log Manual Payment</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Client Name</label>
            <input
              type="text"
              required
              value={form.clientName}
              onChange={e => setForm(p => ({ ...p, clientName: e.target.value }))}
              className="w-full bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Amount ($)</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={form.amount}
              onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              className="w-full bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
            <select
              value={form.method}
              onChange={e => setForm(p => ({ ...p, method: e.target.value }))}
              className="w-full bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            >
              {METHOD_OPTIONS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
            <input
              type="text"
              value={form.note}
              onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
              className="w-full bg-white text-gray-900 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 rounded-lg py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#C9A84C] text-black rounded-lg py-2 text-sm font-semibold hover:bg-[#b8943d] disabled:opacity-50">
              {saving ? 'Saving…' : 'Log Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function buildMonthlyData(payments) {
  return Array.from({ length: 12 }, (_, i) => {
    const d = subMonths(new Date(), 11 - i);
    const label = format(d, 'MMM');
    const total = payments
      .filter(p => {
        const pd = new Date(p.created_at);
        return isSameMonth(pd, d);
      })
      .reduce((s, p) => s + (p.amount || 0), 0);
    return { month: label, Revenue: total };
  });
}

function exportCSV(payments) {
  const rows = [
    ['Date', 'Client', 'Amount', 'Method', 'Status'],
    ...payments.map(p => {
      const d = new Date(p.created_at);
      return [format(d, 'yyyy-MM-dd'), p.client_name || '', p.amount || 0, p.method || '', p.status || ''];
    }),
  ];
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'payments.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function Payments() {
  const { payments, loading } = usePayments();
  const [showLog, setShowLog] = useState(false);

  const stats = useMemo(() => {
    const now = new Date();
    const mtdStart = startOfMonth(now);
    const ytdStart = new Date(now.getFullYear(), 0, 1);

    const mtd = payments.filter(p => {
      const d = new Date(p.created_at);
      return d >= mtdStart;
    }).reduce((s, p) => s + (p.amount || 0), 0);

    const ytd = payments.filter(p => {
      const d = new Date(p.created_at);
      return d >= ytdStart;
    }).reduce((s, p) => s + (p.amount || 0), 0);

    const paid = payments.filter(p => p.status === 'paid');
    const avg = paid.length ? Math.round(paid.reduce((s, p) => s + (p.amount || 0), 0) / paid.length) : 0;
    const pending = payments.filter(p => p.status === 'pending').reduce((s, p) => s + (p.amount || 0), 0);

    return { mtd, ytd, avg, pending };
  }, [payments]);

  const chartData = useMemo(() => buildMonthlyData(payments), [payments]);

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Payments</h2>
        <div className="flex gap-2">
          <button
            onClick={() => exportCSV(payments)}
            className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            <Download size={15} /> Export CSV
          </button>
          <button
            onClick={() => setShowLog(true)}
            className="flex items-center gap-2 bg-[#C9A84C] text-black px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-[#b8943d]"
          >
            <Plus size={15} /> Log Payment
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Revenue MTD', value: `$${stats.mtd.toLocaleString()}` },
          { label: 'Revenue YTD', value: `$${stats.ytd.toLocaleString()}` },
          { label: 'Avg Ticket', value: `$${stats.avg}` },
          { label: 'Deposits Pending', value: `$${stats.pending.toLocaleString()}` },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs text-gray-400 uppercase tracking-wide">{s.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-4">Monthly Revenue</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
            <Tooltip formatter={v => `$${v}`} />
            <Bar dataKey="Revenue" fill="#C9A84C" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Transactions table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Method</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Amount</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                    No payments yet.
                  </td>
                </tr>
              ) : (
                payments.map(p => {
                  const d = new Date(p.created_at);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">{format(d, 'MMM d, yyyy')}</td>
                      <td className="px-4 py-3 text-gray-800">{p.client_name || '—'}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-500">{p.method || '—'}</td>
                      <td className={`px-4 py-3 font-semibold ${p.amount >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {p.amount >= 0 ? '+' : ''}${Math.abs(p.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          p.status === 'paid' ? 'bg-emerald-100 text-emerald-800'
                          : p.status === 'pending' ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {p.status || 'paid'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {showLog && <LogPaymentModal onClose={() => setShowLog(false)} />}
    </div>
  );
}
